import Store from '@/store';
import {
  Getters,
  Mutations
} from '@/store/types';
import { ApiService } from '@/services/ApiService';
import { OrderDto, } from '@eurogast/hub-client-typescript';
import { ConfigService } from '@/services/ConfigService';
import Vue from 'vue';
import { LockService } from '@/services/LockService';
import Big from 'big.js';
import {
  Cart,
  CartItem
} from '@/services/cart/model/cart';
import { CartEvents } from '@/services/cart/model/cart-events';
import { CartApi } from '@/services/cart/api/CartApi';
import { Step } from '@/cart-page/header/Step';

export abstract class CartServiceBase {

  protected api: CartApi;

  protected constructor(api: CartApi) {
    this.api = api;
  }
  /**
   * Causes the current remote cart to be fetched to the store if the current user is authenticated.
   */
  public fetchCart() {
    if (!ConfigService.hasAccessToken()) {
      return;
    }
    this.api.getOwnShoppingCart(...this.common())
      .then((cart) => this.storeCartState(cart))
      .catch(ApiService.defaultErrorHandler);
    Vue.prototype.$events.$once('customer-change', () => {
      this.fetchCart();
    });
  }

  /**
   * Puts given item into the remote cart if it is different from the store state. If the current user is not
   * authenticated, they are redirected to the login page.
   * @param forceUpdate whether to forceUpdate the cart (e.g on optimistic beforeunload cart update)
   * @param item the item to put
   */
  public putItem(item: CartItem, forceUpdate?: boolean) {
    const { sku, amount, unitAbbreviation, ...rest } = item;
    if (!ConfigService.hasAccessToken()) {
      document.location.href = '/auth/login';
      return;
    }
    const existing = this.getItem(sku);
    if (!existing ||
      (existing.amount !== amount || existing.unitAbbreviation !== unitAbbreviation || forceUpdate)) {
        this.api.addItemToOwnShoppingCart(...this.commonAnd(
          CartItem.buildWithDefaults(item)
        )
      )
        .then((cart) => this.storeCartState(cart))
        .then((e) => {
          Vue.prototype.$events.$emit(CartEvents.ITEM_SAVED, item);
        })
        .catch(ApiService.defaultErrorHandler);
    }
  }

  /**
   * Puts given item into the remote cart if it is different from the store state. If the current user is not
   * authenticated, they are redirected to the login page.
   * @param item the item to put
   * @param lockService lockservice if we want to make request ever so often
   */
  public putItemWithLock(item: CartItem, lockService: LockService) {
    if (!ConfigService.hasAccessToken()) {
      document.location.href = '/auth/login';
      return;
    }
    const existing = this.getItem(item.sku);
    if (!existing ||
      (existing.amount !== item.amount || existing.unitAbbreviation !== item.unitAbbreviation)) {

      const emit = (callIndex?: number) => {
        const itemLater = this.getItem(item.sku);
        this.api.addItemToOwnShoppingCartNoDetail(
          ...this.commonAnd(
            CartItem.buildWithDefaults(itemLater)
          )
        ).then((resItem) => {
          if (lockService && lockService.areOutstandingCalls(callIndex)) {
            // don't update, there are pending changes to be stored -> preventing model reset on long reply
            return;
          }
          this.setItemPartial(resItem);
        }).catch(ApiService.defaultErrorHandler);
      };

      this.setItemPartial(item);
      lockService.lockAndRelease(emit.bind(CartServiceBase));
    }
  }

  /**
   * Puts given item into the local cart. If the current user is not
   * authenticated, they are redirected to the login page.
   * @param item the item to put
   */
  public putLocalItem(item: CartItem) {
    if (!ConfigService.hasAccessToken()) {
      document.location.href = '/auth/login';
      return;
    }
    const existing = this.getItem(item.sku);
    if (!existing ||
      (existing.amount !== item.amount || existing.unitAbbreviation !== item.unitAbbreviation)) {
      this.storeToLocalCartStore(item);
    }
  }

  /**
   * Removes given item from the remote cart, even if it is not in the store state or the current user is not
   * authenticated.
   * @param item the item to put
   */
  public removeItem(item: CartItem) {
    Vue.prototype.$events.$emit(CartEvents.ITEM_REMOVED, item);
    // this does not check authentication because where would they get an item without authentication?
    return this.api.removeItemFromOwnShoppingCart(...this.commonAnd(item))
      .then((cart) => this.storeCartState(cart))
      .catch(ApiService.defaultErrorHandler);
  }

  /**
   * Returns the item currently in the cart with given sku or undefined if there is no such item in the cart.
   * @param sku the sku to search for
   */
  public getItem(sku: string): CartItem {
    return Store.getters[Getters.Cart.getItemBySku](sku);
  }

  /**
   * Returns the item currently in the cart with given sku or undefined if there is no such item in the cart.
   */
  public getAllItems(): CartItem[] {
    return Store.getters[Getters.Cart.getAllItems]();
  }

  /**
   * Returns the total price of all items in the cart
   */
  public get totalPrice(): Big {
    return Store.getters[Getters.Cart.getTotalPrice]();
  }

  /**
   * Clears all items currently in the cart.
   */
  public clearItems() {
    this.api.clearOwnShoppingCart(...this.common())
      .then((cart) => this.storeCartState(cart))
      .catch(ApiService.defaultErrorHandler);
  }

  public nextStepCartCheckout() {
    const state = Store.state.cart;
    if (state.step === Step.DELIVERY) {
      this.submitOrder().then((order: OrderDto) => {
            this.storeSetLastSuccessfulOrder(order);
            this.storeNextCheckoutStep();
          }
      ).catch(ApiService.defaultErrorHandler);
    } else {
      this.storeNextCheckoutStep();
    }
  }

  protected abstract submitOrder(): Promise<OrderDto>;

  protected storeSetLastSuccessfulOrder(orderDto: OrderDto) {
    Store.commit({ type: Mutations.Cart.setLastSuccessfulOrder, lastSuccessfulOrder: orderDto});
  }

  protected storeNextCheckoutStep() {
    Store.commit(Mutations.Cart.nextStep);
  }

  protected setItemPartial(item) {
    Store.commit(Mutations.Cart.setItemPartial, item);
    Vue.prototype.$events.$emit(CartEvents.CART_UPDATE);
  }

  protected storeToLocalCartStore(item: CartItem) {
    Store.commit({ type: Mutations.Cart.addToLocalCart, cartItem: item });
    Vue.prototype.$events.$emit(CartEvents.CART_UPDATE);
  }

  protected storeCartState(resCart: Cart) {
    Store.commit({ type: Mutations.Cart.setLocalCart, cart: resCart });
    Vue.prototype.$events.$emit(CartEvents.CART_UPDATE);
  }

  private common(): [string, string] {
    return [ConfigService.getTenant(), ConfigService.currentCustomerId];
  }

  private commonAnd(item: CartItem): [string, string, CartItem] {
    const common = this.common();
    return [common[0], common[1], item];
  }
}
